import { DateTime } from "luxon"

import { authFetch } from "@/api/authFetch.ts"
import { API_BASE } from "@/config.ts"
import type { DataPointFilter, DataPointValue } from "@/types/dataPointTypes.ts"

import type { DataPointReport, DataPointReportRow, ReportRange } from "../../types/reportTypes.ts"

export class ReportRenderer {
  public async getReport(
    report: DataPointReport | undefined,
    page: number,
    pageSize?: number,
  ): Promise<DataPointReportRow[]> {
    if (report == undefined) return []
    const filterArray: DataPointFilter[] = []
    this.getRange(filterArray, report.range)
    filterArray.push({ filter: "interval", value: report.interval.toString() })
    filterArray.push({ filter: "page", value: page.toString() })
    if (pageSize) filterArray.push({ filter: "pageSize", value: pageSize.toString() })
    const data = await this.getData(filterArray, report)
    return this.transform(data)
  }

  private getRange(filterArray: DataPointFilter[], range: ReportRange) {
    switch (range.$type) {
      case "constant": {
        filterArray.push({ filter: "start", value: range.start.toISOString() })
        filterArray.push({ filter: "end", value: range.end.toISOString() })
        break
      }

      case "fromNow": {
        const start = DateTime.now().minus({ seconds: range.returnRange }).toISO()
        const end = DateTime.now().toISO()

        filterArray.push({ filter: "start", value: start })
        filterArray.push({ filter: "end", value: end })
        break
      }
      case "periodic": {
        if (!range.period) return
        if (!range.timezone) return
        if (range.current) {
          const start = DateTime.now().setZone(range.timezone).startOf(range.period).toISO()
          const end = DateTime.now().setZone(range.timezone).endOf(range.period).toISO()
          if (!start || !end) return
          filterArray.push({ filter: "start", value: start })
          filterArray.push({ filter: "end", value: end })
        } else {
          const start = DateTime.now()
            .minus({ [range.period + "s"]: 1 })
            .setZone(range.timezone)
            .startOf(range.period)
            .toISO()
          const end = DateTime.now()
            .minus({ [range.period + "s"]: 1 })
            .setZone(range.timezone)
            .endOf(range.period)
            .toISO()
          if (!start || !end) return
          filterArray.push({ filter: "start", value: start })
          filterArray.push({ filter: "end", value: end })
        }
        break
      }
    }
  }

  private async getData(
    filterArray: DataPointFilter[],
    report: DataPointReport,
  ): Promise<DataPointValue[][]> {
    if (!this.validateFilter(filterArray)) return []
    const filterString = this.getFilterString(filterArray)
    const responses = await Promise.all(
      report.dataPoints.map((id, index) =>
        authFetch(
          `${API_BASE}/datapoints/${id}/intervals?${filterString}&aggMode=${report.aggModes[index]}`,
        ),
      ),
    )
    const data: DataPointValue[][] = await Promise.all(responses.map((res) => res.json()))
    return data
  }

  private transform(data: DataPointValue[][]): DataPointReportRow[] {
    return (
      data[0]?.map((v, index) => ({
        timestamp: v.timestamp,
        values: data.map((dp) => dp[index]?.value || 0),
      })) || []
    )
  }

  private getFilterString(filterArray: DataPointFilter[]): string {
    return filterArray.map((f) => `${f.filter}=${f.value}`).join("&")
  }

  private validateFilter(filterArray: DataPointFilter[]): boolean {
    if (filterArray.findIndex((f) => f.filter == "start") == -1) return false
    if (filterArray.findIndex((f) => f.filter == "end") == -1) return false
    if (filterArray.findIndex((f) => f.filter == "interval") == -1) return false
    if (Number(filterArray.find((f) => f.filter == "interval")?.value) < 1) return false
    if (
      new Date(filterArray.find((f) => f.filter == "start")?.value || "") >=
      new Date(filterArray.find((f) => f.filter == "end")?.value || "")
    )
      return false
    return true
  }
}
